import { Box } from "@mui/material";
import { useQuery } from "@apollo/client/react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import FullScreenLoading from "@/components/FullScreenLoading";
import { NewRelicSession } from "@/config/newrelic/NewRelicSession";
import { ROUTE_PARAMS } from "@/constants";
import { useSupportMode } from "@/context/supportMode";
import { isActiveSupportModeForRouteAccount } from "@/context/supportMode/supportModeAuthHeaders";
import { gql } from "@/gql";
import AccessDenied from "@/pages/AccessDenied";
import { PermissionProvider } from "@/permissions";
import { applyAccountIdAuthPolicyForRole } from "@/provider/AuthProvider/accountIdAuthPolicy";
import { pageLayout } from "@/theme/pageLayout";
import { isActiveSupportSessionTimeoutError } from "@/utils/supportSessionTimeoutError";
import { MeContext, type MeUser } from "./MeContext";

export const ME = gql(`
  query Me {
    me {
      id
      email
      firstName
      lastLoginDate
      lastName
      role
      status
      permissionList {
        action
        category
        policy
      }
    }
  }
`);

type MeProviderProps = {
  children?: ReactNode;
};

export const MeProvider = (props: MeProviderProps) => {
  const { children } = props;
  const { t } = useTranslation();
  const { [ROUTE_PARAMS.ACCOUNT_ID]: accountId } = useParams();
  const { session: supportModeSession } = useSupportMode();

  const isActiveSupportMode = isActiveSupportModeForRouteAccount(
    supportModeSession,
    accountId
  );

  const { data, loading, error } = useQuery(ME, {
    context: { skipGlobalError: true },
    ...(isActiveSupportMode ? { fetchPolicy: "network-only" } : {}),
  });

  const meRole = data?.me.role;

  useEffect(() => {
    if (meRole != null) {
      applyAccountIdAuthPolicyForRole(meRole);
    }
  }, [meRole]);

  const renderMeProvider = (me: MeUser) => {
    return (
      <MeContext.Provider
        value={{
          me,
        }}
      >
        <PermissionProvider permissionList={me.permissionList} role={me.role}>
          <NewRelicSession />
          {children}
        </PermissionProvider>
      </MeContext.Provider>
    );
  };

  if (loading && !data?.me) {
    return <FullScreenLoading title={t("me.loading")} />;
  }

  const me = data?.me;
  const isSessionTimeout =
    error !== undefined && isActiveSupportSessionTimeoutError(error);

  if (isSessionTimeout) {
    if (me) {
      return renderMeProvider(me);
    }

    return null;
  }

  if (error || !me) {
    return (
      <Box sx={pageLayout}>
        <AccessDenied variant="meForbidden" />
      </Box>
    );
  }

  return renderMeProvider(me);
};
